/**
 * loaderPreProcessJson_Inheritance.ts - JSON Inheritance Support methods for LoaderPreProcessJson
 * Uses context stack for inheritance resolution (supports multi-parent scenarios)
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { InheritanceOperation } from '../../../../Assembler/dist/model/index.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/index.js';
import { JsonValue, JsonValueType } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { LoaderPreProcessJson } from './loaderPreProcessJson.js';
import type { TargetFile } from '../../model/targetFile.js';


type PlainJsonObject = { [key: string]: unknown };
type PlainJsonMap = globalThis.Map <string, unknown>;

/**
 * Gets DataJson with inheritance resolution
 * Resolves keys ending with # by searching up the context stack (supports multi-parent scenarios)
 */
export function getTemplateDataWithInheritance(
    appSite: string,
    appView: string,
    appFile: string,
    dataJson: JsonObject,
    resolvedTemplateName: string,
    flowStack: string[] | null = null,
    loader: LoaderPreProcessJson,
    resolvedAppSite: string | null = null,
    configJson: JsonObject | null = null,
    htmlJson: JsonObject | null = null,
    clientMode: boolean = false
): JsonObject | null {
    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`_getTemplateDataJsonWithInheritance: appSite=${appSite}, resolvedTemplateName=${resolvedTemplateName}, flowStack=${flowStack ? flowStack.length : 0}`, 'LoaderPreProcessJson');
    }

    const dataJsonLength = dataJson instanceof Map ? dataJson.size : Object.keys(dataJson).length;
    if (!dataJson || dataJsonLength === 0) {
        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`No DataJson found for resolvedTemplateName=${resolvedTemplateName}`, 'LoaderPreProcessJson');
        }
        return null;
    }

    const resolvedJson = new JsonObject();

    // Handle both plain objects and Map-like objects (JsonObject)
    const dataJsonEntries: Iterable<[string, unknown]> = dataJson instanceof Map ? dataJson.entries() : Object.entries(dataJson);
    for (const [key, rawValue] of dataJsonEntries) {
        // Unwrap JsonValue to raw value for type checks
        const value = rawValue instanceof JsonValue ? (rawValue.asString() ?? rawValue) : rawValue;
        if (key.endsWith('#') && typeof value === 'string') {
            const actualKey = key.substring(0, key.length - 1);
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`Found inheritance key: ${key}, defaultValue=${value}, resolving for actualKey=${actualKey}`, 'LoaderPreProcessJson');
            }

            const templateKey = `${appSite.toLowerCase()}_${resolvedTemplateName.toLowerCase()}`;
            const resolvedTemplateJsonMap = loader._dynamicResultsJson.resolvedTemplateJson;
            const { resolvedValue, sourceTemplate, wasInherited } = resolveDataJsonKeyWithInheritance(actualKey, value, templateKey, loader._dataJsonCache, appSite, loader._config!.maxRecursionDepth, flowStack, resolvedTemplateJsonMap);

            if (resolvedValue !== null) {
                resolvedJson.set(actualKey, resolvedValue);
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`Resolved inherited key ${key} -> ${actualKey} = ${resolvedValue}`, 'LoaderPreProcessJson');
                }

                // Track inheritance operation for flow data (capture full context path for tree building)
                if (loader._inheritanceOperations) {
                    loader._inheritanceOperations.push(new InheritanceOperation({
                        targetTemplate: resolvedTemplateName,
                        contextPath: flowStack ? [...flowStack] : [],
                        inheritKey: actualKey,
                        sourceTemplate: sourceTemplate || '',
                        defaultValue: value,
                        resolvedValue: resolvedValue,
                        wasInherited: wasInherited
                    }));
                }

                continue;
            }
        }

        resolvedJson.set(key, rawValue);
    }

    // Apply global data after inheritance resolution, before DynamicData handlers
    applyGlobalData(loader, appSite, resolvedTemplateName, resolvedJson, resolvedAppSite);

    // Apply dynamic data handlers if configured (matches C# behavior)
    // Pass flowStack for parent value inheritance via callback
    const componentDepth = flowStack ? flowStack.length : 0;
    const effectiveResolvedAppSite = resolvedAppSite || appSite;
    loader.applyDynamicDataHandlers(appSite, appView, appFile, resolvedTemplateName, componentDepth, effectiveResolvedAppSite, resolvedTemplateName, resolvedJson, configJson, flowStack);

    // Client-data preparer (dynamicclientdata.json): produce the RAW set for browser-side filtering into
    // dynamicClientDataResults. Runs alongside the server data handlers but never mutates resolvedJson.
    loader.applyDynamicClientDataHandlers(appSite, appView, appFile, resolvedTemplateName, componentDepth, effectiveResolvedAppSite, resolvedTemplateName, resolvedJson, configJson, flowStack);

    // CLIENT tier consumption: fill resolvedJson from the delivered island via the registered client handler
    // (substitutes for the server data handler that ConfigClient does not ship) and re-emit the island at the
    // page tail — so the client re-merge matches the server render byte-for-byte.
    if (clientMode) {
        loader.applyClientHandlers(appSite, appView, appFile, resolvedTemplateName, componentDepth, effectiveResolvedAppSite, resolvedTemplateName, htmlJson, resolvedJson, configJson, flowStack);
    }

    if (loader.getActionMode()) {
        loader.applyDynamicActionHandlers(appSite, appView, appFile, resolvedTemplateName, effectiveResolvedAppSite, resolvedTemplateName, resolvedJson, configJson, flowStack);
        loader.applyDynamicDataHandlers(appSite, appView, appFile, resolvedTemplateName, componentDepth, effectiveResolvedAppSite, resolvedTemplateName, resolvedJson, configJson, flowStack);
    }

    // Store resolved JSON for child component inheritance (uses same key pattern as searchContextStackForKey)
    loader._dynamicResultsJson.resolvedTemplateJson.set(resolvedTemplateName.toLowerCase(), resolvedJson);

    return resolvedJson;
}

export function hasGlobalData(loader: LoaderPreProcessJson, appSite: string, componentName: string, resolvedAppSite: string | null): boolean {
    const targetAppSite = resolvedAppSite || appSite;
    return getGlobalDataWithFallback(loader, targetAppSite, appSite) !== null
        || getGlobalViewDataForComponentWithFallback(loader, targetAppSite, appSite, componentName) !== null;
}

// #region Global Data (Private)

/**
 * Applies globaldata and globalviewdata values to component JSON
 * Precedence: Component JSON < globaldata < globalviewdata (component-specific)
 * Placeholders like {{GlobalKey}} are replaced with actual values
 */
function applyGlobalData(
    loader: LoaderPreProcessJson,
    appSite: string,
    componentName: string,
    componentJson: JsonObject | PlainJsonObject,
    resolvedAppSite: string | null
): void {
    const targetAppSite = resolvedAppSite || appSite;

    // 1. Get globaldata (lowest global precedence) with SearchAppSites fallback
    const globalData = getGlobalDataWithFallback(loader, targetAppSite, appSite);

    // 2. Get globalviewdata (component-specific, higher precedence) with SearchAppSites fallback
    const globalViewData = getGlobalViewDataForComponentWithFallback(loader, targetAppSite, appSite, componentName);

    // 3. Merge in order: globaldata first, then globalviewdata
    mergeGlobalDataIntoComponent(componentJson, globalData);
    mergeGlobalDataIntoComponent(componentJson, globalViewData);
}

/**
 * Shared fallback+merge logic for global data retrieval
 */
function getWithFallback(
    loader: LoaderPreProcessJson,
    targetAppSite: string,
    appSite: string,
    getter: (site: string) => JsonObject | PlainJsonObject | null
): JsonObject | PlainJsonObject | null {
    // Non-merge mode: return first found
    if (!loader._config!.globalDataMergeFromFallback) {
        let result = getter(targetAppSite);
        if (result !== null) return result;

        if (targetAppSite !== appSite) {
            result = getter(appSite);
            if (result !== null) return result;
        }

        const searchAppSites = loader.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites && searchAppSites.length > 0) {
            const searches = searchAppSites.split(',');
            for (let i = 0; i < searches.length; i++) {
                const trimmed = searches[i].trim();
                if (!trimmed) continue;
                result = getter(trimmed);
                if (result !== null) return result;
            }
        }
        return null;
    }

    // Merge mode: collect all sources and merge
    const sources: Array<JsonObject | PlainJsonObject> = [];

    const targetData = getter(targetAppSite);
    if (targetData !== null) {
        sources.push(targetData);
    }

    if (targetAppSite !== appSite) {
        const appSiteData = getter(appSite);
        if (appSiteData !== null) {
            sources.push(appSiteData);
        }
    }

    const searchAppSites = loader.getSearchAppSitesForAppSite(appSite);
    if (searchAppSites && searchAppSites.length > 0) {
        const searches = searchAppSites.split(',');
        for (let i = 0; i < searches.length; i++) {
            const trimmed = searches[i].trim();
            if (!trimmed) continue;
            const searchData = getter(trimmed);
            if (searchData !== null) {
                sources.push(searchData);
            }
        }
    }

    if (sources.length === 0) return null;
    if (sources.length === 1) return sources[0];

    // Merge all sources
    const merged = new JsonObject();
    const overwrite = loader._config!.globalDataMergeOverwrite;
    for (let i = 0; i < sources.length; i++) {
        const source = sources[i];
        // Handle both Map (JsonObject) and plain object sources
        const entries: Iterable<[string, unknown]> = source instanceof Map ? source.entries() : Object.entries(source);
        for (const [key, value] of entries) {
            if (!merged.has(key) || overwrite) {
                merged.set(key, value);
            }
        }
    }
    return merged;
}

function getGlobalDataWithFallback(
    loader: LoaderPreProcessJson,
    targetAppSite: string,
    appSite: string
): JsonObject | PlainJsonObject | null {
    return getWithFallback(loader, targetAppSite, appSite, (site: string) => loader.getGlobalData(site));
}

function getGlobalViewDataForComponentWithFallback(
    loader: LoaderPreProcessJson,
    targetAppSite: string,
    appSite: string,
    componentName: string
): JsonObject | PlainJsonObject | null {
    return getWithFallback(loader, targetAppSite, appSite, (site: string) => loader.getGlobalViewDataForComponent(site, componentName));
}

export function findKeyIgnoreCase(data: JsonObject, key: string): string | null {
    if (data.has(key)) return key;
    const keyLower = key.toLowerCase();
    for (const existingKey of data.keys()) {
        if (existingKey.toLowerCase() === keyLower) {
            return existingKey;
        }
    }
    return null;
}

export function getScalarString(val: JsonValue): string | null {
    if (val.typeName === JsonValueType.String) return val.asString();
    if (val.typeName === JsonValueType.Integer) return val.asInteger()?.toString() ?? null;
    if (val.typeName === JsonValueType.Number) return val.asNumber()?.toString() ?? null;
    if (val.typeName === JsonValueType.Bool) return val.asBool() === true ? 'true' : 'false';
    return null;
}

/**
 * Merges global data into component JSON
 * Handles {{placeholder}} pattern replacement and direct key overwrites
 * Supports both plain objects and JsonObject (Map-based)
 */
function mergeGlobalDataIntoComponent(
    componentJson: JsonObject | PlainJsonObject,
    globalData: JsonObject | PlainJsonObject | null
): void {
    if (globalData === null) {
        return;
    }
    // Check if globalData has any keys
    const globalDataKeys = globalData instanceof Map ? Array.from(globalData.keys()) : Object.keys(globalData);
    if (globalDataKeys.length === 0) {
        return;
    }

    // Helper: case-insensitive key lookup in globalData
    const isMap = globalData instanceof Map;
    const hasKey = (k: string): boolean => isMap ? (globalData as PlainJsonMap).has(k) : (k in globalData);
    const getValueByKey = (k: string): unknown => isMap ? (globalData as PlainJsonMap).get(k) : (globalData as PlainJsonObject)[k];
    const getAllKeys = (): string[] => isMap ? Array.from((globalData as PlainJsonMap).keys()) : Object.keys(globalData);
    const findKeyIgnoreCase = (k: string): string | null => {
        if (hasKey(k)) return k;
        const lowerKey = k.toLowerCase();
        const keys = getAllKeys();
        for (let j = 0; j < keys.length; j++) {
            if (keys[j].toLowerCase() === lowerKey) return keys[j];
        }
        return null;
    };

    // Get keys from componentJson (handle both Map and plain object)
    const keysToProcess: string[] = componentJson instanceof Map ? Array.from(componentJson.keys()) : Object.keys(componentJson);

    for (let i = 0; i < keysToProcess.length; i++) {
        const key = keysToProcess[i];
        // Get value from componentJson (handle both Map and plain object)
        const rawValue: unknown = componentJson instanceof Map ? componentJson.get(key) : (componentJson as PlainJsonObject)[key];

        // Extract string value - handle both raw strings and JsonValue wrappers
        let strValue: string | null = null;
        if (typeof rawValue === 'string') {
            strValue = rawValue;
        } else if (rawValue instanceof JsonValue && rawValue.typeName === JsonValueType.String) {
            strValue = rawValue.asString();
        }

        if (strValue !== null) {
            // Check for {{placeholder}} pattern
            if (strValue.startsWith('{{') && strValue.endsWith('}}')) {
                const placeholderKey = strValue.substring(2, strValue.length - 2);
                const resolvedKey = findKeyIgnoreCase(placeholderKey);
                if (resolvedKey !== null) {
                    const globalValue = getValueByKey(resolvedKey);
                    if (globalValue !== undefined) {
                        // Set value in componentJson (handle both Map and plain object)
                        if (componentJson instanceof Map) {
                            componentJson.set(key, globalValue);
                        } else {
                            (componentJson as PlainJsonObject)[key] = globalValue;
                        }
                    }
                }
            } else if (strValue.includes('{{')) {
                let result = strValue;
                let startIdx = 0;
                while (true) {
                    const pos = result.indexOf('{{', startIdx);
                    if (pos < 0) break;
                    const endPos = result.indexOf('}}', pos + 2);
                    if (endPos < 0) break;
                    const placeholderKey = result.substring(pos + 2, endPos);
                    const resolvedInlineKey = findKeyIgnoreCase(placeholderKey);
                    if (resolvedInlineKey !== null) {
                        const globalValue = getValueByKey(resolvedInlineKey);
                        if (globalValue !== undefined && typeof globalValue === 'string') {
                            result = result.substring(0, pos) + globalValue + result.substring(endPos + 2);
                            startIdx = pos + globalValue.length;
                            continue;
                        }
                    }
                    startIdx = endPos + 2;
                }
                if (result !== strValue) {
                    if (componentJson instanceof Map) {
                        componentJson.set(key, result);
                    } else {
                        (componentJson as PlainJsonObject)[key] = result;
                    }
                }
            }
        }
        else {
            // Array append/skip: if component has an array and globalData has the same key as array,
            // append items from globalData that don't already exist in the component array
            let isArray = false;
            if (rawValue instanceof JsonValue && rawValue.typeName === JsonValueType.Array) {
                isArray = true;
            } else if (Array.isArray(rawValue)) {
                isArray = true;
            }
            const resolvedArrayKey = findKeyIgnoreCase(key);
            if (isArray && resolvedArrayKey !== null) {
                const globalValue = getValueByKey(resolvedArrayKey);
                if (globalValue !== undefined) {
                    let globalIsArray = false;
                    if (globalValue instanceof JsonValue && globalValue.typeName === JsonValueType.Array) {
                        globalIsArray = true;
                    } else if (Array.isArray(globalValue)) {
                        globalIsArray = true;
                    }
                    if (globalIsArray) {
                        // Get arrays
                        let componentArray: unknown[] | null = null;
                        if (rawValue instanceof JsonValue) {
                            componentArray = rawValue.asArray() as unknown as unknown[];
                        } else {
                            componentArray = rawValue as unknown[];
                        }
                        let globalArray: unknown[] | null = null;
                        if (globalValue instanceof JsonValue) {
                            globalArray = globalValue.asArray() as unknown as unknown[];
                        } else {
                            globalArray = globalValue as unknown[];
                        }

                        // Check for optional ArrayNamePrimary
                        const primaryKeyName = key + 'Primary';
                        let primaryKey: string | null = null;
                        const pkRawValue: unknown = componentJson instanceof Map ? componentJson.get(primaryKeyName) : (componentJson as PlainJsonObject)[primaryKeyName];
                        if (pkRawValue !== null && pkRawValue !== undefined) {
                            const pkStr = (pkRawValue instanceof JsonValue) ? pkRawValue.asString() : (typeof pkRawValue === 'string' ? pkRawValue : null);
                            if (pkStr) primaryKey = pkStr;
                        }

                        // Build new array to avoid mutating shared cached data
                        if (componentArray && globalArray) {
                            const newArray = componentArray.slice();
                            for (let g = 0; g < globalArray.length; g++) {
                                const globalItem = globalArray[g];
                                const globalObj = (globalItem instanceof JsonValue) ? globalItem.asObject() : (typeof globalItem === 'object' && globalItem !== null && !Array.isArray(globalItem) ? globalItem : null);
                                if (!globalObj) continue;

                                let existsInComponent = false;
                                for (let c = 0; c < newArray.length; c++) {
                                    const compItem = newArray[c];
                                    const compObj = (compItem instanceof JsonValue) ? compItem.asObject() : (typeof compItem === 'object' && compItem !== null && !Array.isArray(compItem) ? compItem : null);
                                    if (!compObj) continue;

                                    if (MatchesByStringKeys(compObj as PlainJsonObject | PlainJsonMap, globalObj as PlainJsonObject | PlainJsonMap, primaryKey)) {
                                        existsInComponent = true;
                                        break;
                                    }
                                }

                                if (!existsInComponent) {
                                    newArray.push(globalItem);
                                }
                            }
                            // Assign new array back to componentJson
                            if (componentJson instanceof Map) {
                                componentJson.set(key, newArray);
                            } else {
                                (componentJson as PlainJsonObject)[key] = newArray;
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * Checks if a component item matches a global item by comparing string key-value pairs.
 * If primaryKey is set, only that key is compared. Otherwise all string keys in globalItem must match.
 */
export function MatchesByStringKeys(
    componentItem: PlainJsonObject | PlainJsonMap,
    globalItem: PlainJsonObject | PlainJsonMap,
    primaryKey: string | null
): boolean {
    function getStr(obj: PlainJsonObject | PlainJsonMap, k: string): string | null {
        if (obj instanceof Map) {
            if (!obj.has(k)) return null;
            const v = obj.get(k);
            if (v instanceof JsonValue) {
                if (v.typeName === JsonValueType.String) return v.asString();
                if (v.typeName === JsonValueType.Integer) return v.asInteger()?.toString() ?? null;
                if (v.typeName === JsonValueType.Number) return v.asNumber()?.toString() ?? null;
                if (v.typeName === JsonValueType.Bool) return v.asBool() === true ? 'true' : 'false';
                return null;
            }
            if (typeof v === 'string') return v;
            if (typeof v === 'number') return v.toString();
            if (typeof v === 'boolean') return v ? 'true' : 'false';
            return null;
        }
        if (typeof (obj as PlainJsonObject).has === 'function') {
            if (!(obj as unknown as PlainJsonMap).has(k)) return null;
            const v = (obj as unknown as PlainJsonMap).get(k);
            if (v instanceof JsonValue) {
                if (v.typeName === JsonValueType.String) return v.asString();
                if (v.typeName === JsonValueType.Integer) return v.asInteger()?.toString() ?? null;
                if (v.typeName === JsonValueType.Number) return v.asNumber()?.toString() ?? null;
                if (v.typeName === JsonValueType.Bool) return v.asBool() === true ? 'true' : 'false';
                return null;
            }
            if (typeof v === 'string') return v;
            if (typeof v === 'number') return v.toString();
            if (typeof v === 'boolean') return v ? 'true' : 'false';
            return null;
        }
        const v = (obj as PlainJsonObject)[k];
        if (v === undefined) return null;
        if (v instanceof JsonValue) {
            if (v.typeName === JsonValueType.String) return v.asString();
            if (v.typeName === JsonValueType.Integer) return v.asInteger()?.toString() ?? null;
            if (v.typeName === JsonValueType.Number) return v.asNumber()?.toString() ?? null;
            if (v.typeName === JsonValueType.Bool) return v.asBool() === true ? 'true' : 'false';
            return null;
        }
        if (typeof v === 'string') return v;
        if (typeof v === 'number') return v.toString();
        if (typeof v === 'boolean') return v ? 'true' : 'false';
        return null;
    }

    function getKeys(obj: PlainJsonObject | PlainJsonMap): string[] {
        if (obj instanceof Map) return Array.from(obj.keys());
        if (typeof (obj as PlainJsonObject).keys === 'function') return Array.from((obj as unknown as PlainJsonMap).keys());
        return Object.keys(obj);
    }

    function hasKey(obj: PlainJsonObject | PlainJsonMap, k: string): boolean {
        if (obj instanceof Map) return obj.has(k);
        if (typeof (obj as PlainJsonObject).has === 'function') return (obj as unknown as PlainJsonMap).has(k);
        return k in obj;
    }

    if (primaryKey !== null) {
        const compVal = getStr(componentItem, primaryKey);
        const globalVal = getStr(globalItem, primaryKey);
        if (compVal === null || globalVal === null) return false;
        return compVal === globalVal;
    }

    // Match by ALL string key-value pairs in the global item
    let hasStringKey = false;
    const globalKeys = getKeys(globalItem);
    for (let i = 0; i < globalKeys.length; i++) {
        const k = globalKeys[i];
        const globalStr = getStr(globalItem, k);
        if (globalStr === null) continue;
        hasStringKey = true;
        if (!hasKey(componentItem, k)) return false;
        const compStr = getStr(componentItem, k);
        if (compStr === null) return false;
        if (compStr !== globalStr) return false;
    }
    return hasStringKey;
}

// #endregion

/**
 * Resolves a DataJson key by searching up the context stack (supports multi-parent scenarios)
 */
export function resolveDataJsonKeyWithInheritance(
    actualKey: string,
    defaultValue: string,
    _currentTemplateKey: string,
    dataJsonCache: Map<string, Map<string, TargetFile>>,
    appSite: string,
    _maxRecursionDepth: number,
    flowStack: string[] | null = null,
    resolvedTemplateJson: JsonObject | null = null
): { resolvedValue: string | null; sourceTemplate: string | null; wasInherited: boolean } {
    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`Resolving inherited key: ${actualKey} for template ${_currentTemplateKey}, flowStack=${flowStack ? flowStack.length : 0}`, 'LoaderPreProcessJson');
    }

    // Use context stack for inheritance resolution (supports multi-parent scenarios)
    if (flowStack && flowStack.length > 0) {
        const { value: inheritedValue, sourceTemplate } = searchContextStackForKey(actualKey, flowStack, dataJsonCache, appSite, resolvedTemplateJson);
        if (inheritedValue !== null) {
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`Found inherited value for ${actualKey}: ${inheritedValue} from ${sourceTemplate}`, 'LoaderPreProcessJson');
            }
            return { resolvedValue: inheritedValue, sourceTemplate, wasInherited: true };
        }
    }

    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`No inherited value found for ${actualKey}, using default: ${defaultValue}`, 'LoaderPreProcessJson');
    }
    return { resolvedValue: defaultValue, sourceTemplate: null, wasInherited: false };
}

/**
 * Searches up the context stack to find a DataJson key value
 * Walks UP through context stack (from end toward beginning) to find inherited values
 */
export function searchContextStackForKey(
    key: string,
    flowStack: string[],
    dataJsonCache: Map<string, Map<string, TargetFile>>,
    appSite: string,
    resolvedTemplateJson: JsonObject | null = null
): { value: string | null; sourceTemplate: string | null } {
    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`SearchContextStackForKey: key=${key}, flowStack=[${flowStack.join(',')}]`, 'LoaderPreProcessJson');
    }

    const keyLower = key.toLowerCase();

    // Walk UP through context stack (from end toward beginning)
    for (let i = flowStack.length - 1; i >= 0; i--) {
        const parentName = flowStack[i];
        const parentNameLower = parentName.toLowerCase();

        // FIRST: Check resolved template JSON (has DynamicData results)
        if (resolvedTemplateJson && resolvedTemplateJson.has(parentNameLower)) {
            const resolvedJson = resolvedTemplateJson.get(parentNameLower).asObject();
            if (resolvedJson) {
                // Handle both Map (JsonObject) and plain object
                const entries: Iterable<[string, unknown]> = resolvedJson instanceof Map ? resolvedJson.entries() : Object.entries(resolvedJson);
                for (const [jsonKey, jsonValue] of entries) {
                    if (jsonKey.toLowerCase() === keyLower) {
                        // Extract string value - handle both raw strings and JsonValue wrappers
                        let strValue: string | null = null;
                        if (typeof jsonValue === 'string') {
                            strValue = jsonValue;
                        } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                            strValue = jsonValue.asString();
                        }
                        if (strValue !== null) {
                            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                                Logger.debug(`Found key ${key} in resolved context at ${parentName}: ${strValue}`, 'LoaderPreProcessJson');
                            }
                            return { value: strValue, sourceTemplate: parentName };
                        }
                    }
                }
            }
        }

        // SECOND: Fall back to original cache (static values from disk)
        if (dataJsonCache.has(appSite)) {
            const parentDataJsonCache = dataJsonCache.get(appSite)!;
            const parentDataKey = `${appSite.toLowerCase()}_${parentNameLower}`;

            if (parentDataJsonCache.has(parentDataKey)) {
                const parentTargetFile = parentDataJsonCache.get(parentDataKey)!;

                if (parentTargetFile && parentTargetFile.jsonStructure) {
                    // Handle both Map (JsonObject) and plain object
                    const jsonEntries: Iterable<[string, unknown]> = parentTargetFile.jsonStructure instanceof Map ? parentTargetFile.jsonStructure.entries() : Object.entries(parentTargetFile.jsonStructure);
                    for (const [jsonKey, jsonValue] of jsonEntries) {
                        if (jsonKey.toLowerCase() === keyLower) {
                            let strValue: string | null = null;
                            if (typeof jsonValue === 'string') {
                                strValue = jsonValue;
                            } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                                strValue = jsonValue.asString();
                            }
                            if (strValue !== null) {
                                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                                    Logger.debug(`Found key ${key} in original cache at ${parentName}: ${strValue}`, 'LoaderPreProcessJson');
                                }
                                return { value: strValue, sourceTemplate: parentName };
                            }
                        }
                    }
                }
            }
        }
    }

    // Not found in context stack
    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`Key ${key} not found in context stack`, 'LoaderPreProcessJson');
    }
    return { value: null, sourceTemplate: null };
}

/**
 * Builds an immutable parent context map from the flow stack.
 * Walks forward through flowStack, collecting all parent key-value pairs.
 * Later parents overwrite earlier ones; resolved values overwrite static cache values.
 */
export function buildParentContext(
    flowStack: string[],
    dataJsonCache: Map<string, Map<string, TargetFile>>,
    primaryAppSite: string,
    resolvedTemplateJson: JsonObject | null = null
): Map<string, string> | null {
    if (!flowStack || flowStack.length === 0) {
        return null;
    }

    const context = new Map<string, string>();

    for (let i = 0; i < flowStack.length; i++) {
        const parentName = flowStack[i];
        const parentNameLower = parentName.toLowerCase();

        // FIRST: Static cache values from DataJson TemplateStore
        if (dataJsonCache && dataJsonCache.has(primaryAppSite)) {
            const parentDataJsonCache = dataJsonCache.get(primaryAppSite)!;
            const bpcParentKey = `${primaryAppSite.toLowerCase()}_${parentNameLower}`;
            if (parentDataJsonCache.has(bpcParentKey)) {
                const parentTargetFile = parentDataJsonCache.get(bpcParentKey)!;
                if (parentTargetFile && parentTargetFile.jsonStructure) {
                    const jsonEntries: Iterable<[string, unknown]> = parentTargetFile.jsonStructure instanceof Map ? parentTargetFile.jsonStructure.entries() : Object.entries(parentTargetFile.jsonStructure);
                    for (const [jsonKey, jsonValue] of jsonEntries) {
                        let strValue: string | null = null;
                        if (typeof jsonValue === 'string') {
                            strValue = jsonValue;
                        } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                            strValue = jsonValue.asString();
                        }
                        if (strValue !== null) {
                            context.set(jsonKey.toLowerCase(), strValue);
                        }
                    }
                }
            }
        }

        // SECOND: Resolved values overwrite static
        if (resolvedTemplateJson && resolvedTemplateJson.has(parentNameLower)) {
            const resolvedJson = resolvedTemplateJson.get(parentNameLower).asObject();
            if (resolvedJson) {
                const entries: Iterable<[string, unknown]> = resolvedJson instanceof Map ? resolvedJson.entries() : Object.entries(resolvedJson);
                for (const [jsonKey, jsonValue] of entries) {
                    let strValue: string | null = null;
                    if (typeof jsonValue === 'string') {
                        strValue = jsonValue;
                    } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                        strValue = jsonValue.asString();
                    }
                    if (strValue !== null) {
                        context.set(jsonKey.toLowerCase(), strValue);
                    }
                }
            }
        }
    }

    return context.size > 0 ? context : null;
}
